// Suscripción — elige tu plan y gestiona el pago (Stripe Billing).
//
// El OWNER de la cuenta ve su plan actual y el catálogo de planes de pago. Al
// elegir un plan (mensual o anual) se llama a `/billing/checkout`, que devuelve
// una URL alojada por Stripe a la que se redirige para pagar de forma segura (el
// manejo de la tarjeta nunca pasa por esta app). Al volver, el webhook de Stripe
// ya habrá activado la suscripción; esta vista relee el estado.
//
// "Gestionar suscripción" abre el Billing Portal de Stripe (cambiar tarjeta,
// cambiar de plan o cancelar), también una página alojada por Stripe.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import type { BillingInvoice, BillingSubscription } from '../api/endpoints.js';
import type { Plan } from '../types.js';
import {
  AlertMessage,
  AsyncBoundary,
  ButtonSpinner,
  Card,
  Icon,
  PageHeader,
} from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import { useToast } from '../ui/ToastProvider.js';
import { friendlyError } from '../api/friendlyError.js';

/** Formatea centavos a una cadena de moneda legible (p. ej. 1500 → "$15.00"). */
function formatPrice(cents: number, currency: string): string {
  const amount = (cents / 100).toFixed(2);
  const symbol = currency.toUpperCase() === 'USD' ? '$' : '';
  return `${symbol}${amount} ${currency.toUpperCase()}`;
}

/** Formatea una fecha ISO a una cadena local legible (o "—" si no hay). */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** Etiqueta legible (i18n) del estado de una factura de Stripe. */
function invoiceStatusLabel(t: TFunction, status: string | null): { label: string; tone: string } {
  switch (status) {
    case 'paid':
      return { label: t('subscription.invoiceStatus.paid'), tone: 'text-goldmine-mint' };
    case 'open':
      return { label: t('subscription.invoiceStatus.open'), tone: 'text-priority-urgent' };
    case 'void':
      return { label: t('subscription.invoiceStatus.void'), tone: 'text-on-surface-variant' };
    case 'uncollectible':
      return { label: t('subscription.invoiceStatus.uncollectible'), tone: 'text-priority-urgent' };
    case 'draft':
      return { label: t('subscription.invoiceStatus.draft'), tone: 'text-on-surface-variant' };
    default:
      return { label: status ?? t('subscription.invoiceStatus.unknown'), tone: 'text-on-surface-variant' };
  }
}

/**
 * ¿Se puede ENVIAR esta factura por correo? Stripe solo permite `sendInvoice`
 * en facturas de cobro MANUAL (`collection_method=send_invoice`), que quedan en
 * estado 'open' o 'draft'. Las facturas de suscripción con tarjeta llegan ya
 * 'paid' (cobro automático) y Stripe rechaza enviarlas manualmente, así que para
 * esas no mostramos el botón (evita el error "can be sent manually only when
 * collection_method=send_invoice").
 */
function canEmailInvoice(status: string | null): boolean {
  return status === 'open' || status === 'draft';
}

/** Fila con una etiqueta y su valor para el detalle de la suscripción. */
function DetailRow({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex items-center justify-between gap-space-md">
      <span className="font-body-sm text-body-sm text-on-surface-variant">{label}</span>
      <span className={`font-body-sm text-body-sm font-bold text-right ${tone ?? ''}`}>{value}</span>
    </div>
  );
}

/** Etiqueta legible (i18n) del estado de la suscripción. */
function statusLabel(t: TFunction, status: BillingSubscription['status']): { label: string; tone: string } {
  switch (status) {
    case 'active':
      return { label: t('subscription.subStatus.active'), tone: 'text-goldmine-mint' };
    case 'trialing':
      return { label: t('subscription.subStatus.trialing'), tone: 'text-primary' };
    case 'past_due':
      return { label: t('subscription.subStatus.past_due'), tone: 'text-priority-urgent' };
    case 'canceled':
    default:
      return { label: t('subscription.subStatus.canceled'), tone: 'text-on-surface-variant' };
  }
}

export function SubscriptionView() {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useToast();
  const subscription = useAsync(() => api.billing.subscription(), []);
  const plans = useAsync(() => api.billing.plans(), []);
  const invoices = useAsync(() => api.billing.invoices(), []);

  // Resultado del retorno de Stripe Checkout: `?checkout=success|cancel`. Stripe
  // redirige aquí tras pagar/cancelar; el webhook ya habrá activado la
  // suscripción, así que el estado de arriba refleja el cambio.
  //
  // Caso especial `?checkout=auto&plan=<code>&period=<monthly|yearly>`: llega
  // desde el AUTO-REGISTRO cuando el usuario eligió un plan de PAGO. En ese caso
  // NO redirigimos automáticamente a la pasarela: solo PRE-SELECCIONAMOS el plan
  // elegido y resaltamos su tarjeta, dejando que el usuario revise y confirme el
  // pago con un clic explícito. Así nunca se le arrastra a Stripe sin que pueda
  // elegir la opción correcta.
  const [params, setParams] = useSearchParams();
  const checkoutResult = params.get('checkout');

  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  // Plan pre-seleccionado desde el auto-registro, a la espera de confirmación
  // explícita del usuario antes de ir a la pasarela.
  const [preselected, setPreselected] = useState<{ planCode: string; period: 'monthly' | 'yearly' } | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailingInvoice, setEmailingInvoice] = useState<string | null>(null);
  const [emailSentFor, setEmailSentFor] = useState<string | null>(null);

  /** Pide a Stripe que envíe por correo una factura concreta de la cuenta. */
  async function emailInvoice(invoiceId: string) {
    setEmailingInvoice(invoiceId);
    setEmailSentFor(null);
    setError(null);
    try {
      await api.billing.emailInvoice(invoiceId);
      setEmailSentFor(invoiceId);
      toast.success(t('subscription.invoiceSent'));
    } catch (err) {
      setError(friendlyError(err, t, 'subscription.errEmailInvoice'));
    } finally {
      setEmailingInvoice(null);
    }
  }

  /** Inicia el pago de un plan y redirige a Stripe Checkout. */
  async function startCheckout(planCode: string, period: 'monthly' | 'yearly') {
    setBusyPlan(`${planCode}:${period}`);
    setError(null);
    try {
      const { url } = await api.billing.checkout(planCode, period);
      window.location.assign(url); // redirige a la página alojada de Stripe.
    } catch (err) {
      setError(friendlyError(err, t, 'subscription.errCheckout'));
      setBusyPlan(null);
    }
  }

  // Pre-selección del AUTO-REGISTRO: si llegamos con `?checkout=auto&plan=&period=`,
  // guardamos el plan elegido para resaltarlo y ofrecer un botón de confirmación,
  // pero NUNCA redirigimos solos a la pasarela. Limpiamos esos parámetros de la
  // URL de inmediato para que un refresh no reaplique el estado.
  const autoHandledRef = useRef(false);
  useEffect(() => {
    if (autoHandledRef.current) return;
    if (params.get('checkout') !== 'auto') return;
    const planCode = (params.get('plan') ?? '').trim();
    const periodRaw = params.get('period') === 'monthly' ? 'monthly' : 'yearly';
    autoHandledRef.current = true;
    // Quita los parámetros de auto-checkout de la URL (evita re-aplicar el estado).
    setParams(new URLSearchParams(), { replace: true });
    if (planCode !== '') {
      setPreselected({ planCode, period: periodRaw });
    }
    // Efecto de montaje (una sola vez): lee los parámetros de auto-checkout de
    // la URL al entrar; re-ejecutarlo al cambiar deps re-aplicaría el estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Abre el portal de gestión de suscripción de Stripe. */
  async function openPortal() {
    setPortalBusy(true);
    setError(null);
    try {
      const { url } = await api.billing.portal();
      window.location.assign(url);
    } catch (err) {
      setError(friendlyError(err, t, 'subscription.errPortal'));
      setPortalBusy(false);
    }
  }

  const currentCode = subscription.data?.subscription?.plan?.code ?? null;

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('subscription.pageTitle')}
        subtitle={t('subscription.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />

        {/* Plan pre-seleccionado desde el registro: lo resaltamos y pedimos
            confirmación explícita. NO redirigimos solos a la pasarela. */}
        {preselected && (
          <div className="p-space-sm rounded-lg bg-primary-container/15 border border-primary-container/40 text-primary font-body-sm text-body-sm flex flex-col gap-space-xs sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-space-xs">
              <Icon name="info" className="text-[18px]" />
              {t('subscription.preselected')}
            </span>
          </div>
        )}

        {/* Aviso del retorno de Stripe Checkout (éxito o cancelación). */}
        {checkoutResult === 'success' && (
          <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm flex items-center gap-space-xs">
            <Icon name="check_circle" className="text-[18px]" />
            {t('subscription.checkoutSuccess')}
          </div>
        )}
        {checkoutResult === 'cancel' && (
          <div className="p-space-sm rounded-lg bg-primary-container/15 border border-primary-container/40 text-on-surface-variant font-body-sm text-body-sm flex items-center gap-space-xs">
            <Icon name="info" className="text-[18px]" />
            {t('subscription.checkoutCancel')}
          </div>
        )}

        {/* Estado de la suscripción actual. */}
        <AsyncBoundary state={subscription}>
          {(data) => {
            const sub = data.subscription;
            if (!sub || !sub.plan) {
              return (
                <Card title={t('subscription.yourPlan')} icon="workspace_premium">
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {t('subscription.noActivePlan')}
                  </p>
                </Card>
              );
            }
            const st = statusLabel(t, sub.status);
            return (
              <Card title={t('subscription.detailTitle')} icon="workspace_premium">
                <div className="flex flex-col gap-space-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-title-md text-title-md font-bold">{sub.plan.name}</span>
                    <span className={`font-body-sm text-body-sm font-bold ${st.tone}`}>{st.label}</span>
                  </div>

                  <div className="flex flex-col gap-space-xs border-t border-outline-variant/40 pt-space-sm">
                    <DetailRow label={t('subscription.enrollDate')} value={formatDate(sub.started_at)} />
                    <DetailRow
                      label={t('subscription.cycleValidity')}
                      value={
                        sub.current_period_start || sub.current_period_end
                          ? `${formatDate(sub.current_period_start)} — ${formatDate(sub.current_period_end)}`
                          : '—'
                      }
                    />
                    <DetailRow
                      label={sub.cancel_at_period_end ? t('subscription.cancelDate') : t('subscription.renewDate')}
                      value={formatDate(sub.current_period_end)}
                      tone={sub.cancel_at_period_end ? 'text-priority-urgent' : undefined}
                    />
                    {sub.cancel_at_period_end && (
                      <p className="font-body-sm text-body-sm text-priority-urgent">
                        {t('subscription.willNotRenew')}
                      </p>
                    )}
                  </div>

                  {sub.has_customer && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm self-start mt-space-xs"
                      disabled={portalBusy}
                      onClick={() => void openPortal()}
                    >
                      {portalBusy ? <ButtonSpinner /> : <Icon name="settings" className="text-[16px]" />}
                      {t('subscription.manageSub')}
                    </button>
                  )}
                </div>
              </Card>
            );
          }}
        </AsyncBoundary>

        {/* Facturas de la cuenta (desde Stripe). Solo si hay Customer/suscripción. */}
        {subscription.data?.subscription?.has_customer && (
          <AsyncBoundary state={invoices}>
            {(data) => <InvoicesCard
              invoices={data.invoices}
              emailingInvoice={emailingInvoice}
              emailSentFor={emailSentFor}
              onEmail={emailInvoice}
            />}
          </AsyncBoundary>
        )}

        {/* Catálogo de planes de pago. */}
        <AsyncBoundary state={plans}>
          {(data) => {
            const payable = data.items.filter(
              (p: Plan) => !p.is_default && (p.price_cents_monthly > 0 || p.price_cents_yearly > 0),
            );
            if (payable.length === 0) {
              return (
                <Card title={t('subscription.plansTitle')} icon="sell">
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {t('subscription.noPayablePlans')}
                  </p>
                </Card>
              );
            }
            return (
              <div className="flex flex-col gap-space-md">
                {payable.map((plan: Plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    isCurrent={plan.code === currentCode}
                    busyPlan={busyPlan}
                    preselectedPeriod={
                      preselected && preselected.planCode === plan.code ? preselected.period : null
                    }
                    onChoose={startCheckout}
                  />
                ))}
              </div>
            );
          }}
        </AsyncBoundary>
      </div>
    </div>
  );
}

/** Tarjeta con el historial de facturas de la cuenta y envío por correo. */
function InvoicesCard({
  invoices,
  emailingInvoice,
  emailSentFor,
  onEmail,
}: {
  invoices: BillingInvoice[];
  emailingInvoice: string | null;
  emailSentFor: string | null;
  onEmail: (invoiceId: string) => void;
}) {
  const { t } = useTranslation();
  if (invoices.length === 0) {
    return (
      <Card title={t('subscription.invoicesTitle')} icon="receipt_long">
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {t('subscription.noInvoices')}
        </p>
      </Card>
    );
  }
  return (
    <Card title={t('subscription.invoicesTitle')} icon="receipt_long">
      <div className="flex flex-col divide-y divide-outline-variant/40">
        {invoices.map((inv) => {
          const st = invoiceStatusLabel(t, inv.status);
          const emailing = emailingInvoice === inv.id;
          const sent = emailSentFor === inv.id;
          return (
            <div key={inv.id} className="flex flex-col gap-space-xs py-space-sm first:pt-0 last:pb-0">
              <div className="flex items-center justify-between gap-space-md">
                <div className="flex flex-col">
                  <span className="font-body-md text-body-md font-bold">
                    {inv.number ?? inv.id}
                  </span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant">
                    {formatDate(inv.created)} · {formatPrice(inv.amount_cents, inv.currency)}
                  </span>
                </div>
                <span className={`font-body-sm text-body-sm font-bold ${st.tone}`}>{st.label}</span>
              </div>
              <div className="flex flex-wrap items-center gap-space-xs">
                {inv.hosted_invoice_url && (
                  <a
                    href={inv.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary btn-sm"
                  >
                    <Icon name="open_in_new" className="text-[16px]" />
                    {t('subscription.view')}
                  </a>
                )}
                {inv.invoice_pdf && (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary btn-sm"
                  >
                    <Icon name="download" className="text-[16px]" />
                    {t('subscription.pdf')}
                  </a>
                )}
                {/* "Enviar por correo" solo aplica a facturas de cobro MANUAL
                    (collection_method=send_invoice), que Stripe deja en estado
                    'open'/'draft'. Las facturas de suscripción con tarjeta llegan
                    ya 'paid' (cobro automático) y Stripe rechaza enviarlas
                    manualmente; para esas ofrecemos solo "Ver" y "PDF". */}
                {canEmailInvoice(inv.status) && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={emailing}
                    onClick={() => onEmail(inv.id)}
                  >
                    {emailing ? <ButtonSpinner /> : <Icon name="mail" className="text-[16px]" />}
                    {t('subscription.emailInvoice')}
                  </button>
                )}
                {sent && (
                  <span className="font-body-sm text-body-sm text-goldmine-mint flex items-center gap-space-xs">
                    <Icon name="check_circle" className="text-[16px]" />
                    {t('subscription.invoiceSent')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/** Tarjeta de un plan con botones de pago mensual y anual. */
function PlanCard({
  plan,
  isCurrent,
  busyPlan,
  preselectedPeriod,
  onChoose,
}: {
  plan: Plan;
  isCurrent: boolean;
  busyPlan: string | null;
  /** Periodo pre-seleccionado desde el auto-registro (resalta su botón), o null. */
  preselectedPeriod: 'monthly' | 'yearly' | null;
  onChoose: (code: string, period: 'monthly' | 'yearly') => void;
}) {
  const { t } = useTranslation();
  const name = plan.public_name && plan.public_name.trim() !== '' ? plan.public_name : plan.name;
  const highlighted = preselectedPeriod !== null;
  return (
    <Card
      title={name}
      icon="sell"
      action={
        isCurrent ? (
          <span className="font-body-sm text-body-sm font-bold text-goldmine-mint">{t('subscription.currentPlan')}</span>
        ) : highlighted ? (
          <span className="font-body-sm text-body-sm font-bold text-primary">{t('subscription.yourChoice')}</span>
        ) : undefined
      }
      className={highlighted ? 'ring-2 ring-primary/60' : undefined}
    >
      {plan.description && (
        <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">{plan.description}</p>
      )}
      {highlighted && (
        <p className="font-body-sm text-body-sm text-primary mb-space-md flex items-center gap-space-xs">
          <Icon name="task_alt" className="text-[16px]" />
          {preselectedPeriod === 'monthly' ? t('subscription.choseMonthly') : t('subscription.choseYearly')}
        </p>
      )}
      <div className="flex flex-col gap-space-sm sm:flex-row">
        {plan.price_cents_monthly > 0 && (
          <button
            type="button"
            className={`btn-sm flex-1 ${
              preselectedPeriod === 'monthly' ? 'btn-primary' : highlighted ? 'btn-secondary' : 'btn-primary'
            }`}
            disabled={busyPlan !== null}
            onClick={() => onChoose(plan.code, 'monthly')}
          >
            {busyPlan === `${plan.code}:monthly` ? (
              <ButtonSpinner />
            ) : (
              <Icon name="calendar_month" className="text-[16px]" />
            )}
            {preselectedPeriod === 'monthly' ? t('subscription.continuePrefix') : t('subscription.monthlyPrefix')}
            {formatPrice(plan.price_cents_monthly, plan.currency)}
          </button>
        )}
        {plan.price_cents_yearly > 0 && (
          <button
            type="button"
            className={`btn-sm flex-1 ${preselectedPeriod === 'yearly' ? 'btn-primary' : 'btn-secondary'}`}
            disabled={busyPlan !== null}
            onClick={() => onChoose(plan.code, 'yearly')}
          >
            {busyPlan === `${plan.code}:yearly` ? (
              <ButtonSpinner />
            ) : (
              <Icon name="event_available" className="text-[16px]" />
            )}
            {preselectedPeriod === 'yearly' ? t('subscription.continuePrefix') : t('subscription.yearlyPrefix')}
            {formatPrice(plan.price_cents_yearly, plan.currency)}
          </button>
        )}
      </div>
    </Card>
  );
}
